import  { useState } from "react";
import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import notificationService from "../../services/notificationService";
import { getImageUrl } from "../../utils/commonFunctions";

const NOTIFICATION_IMAGE_ASPECT = 320 / 180;

export default function OneSignalCMS() {
  const [os, setOs] = useState("all");
  const [userStatus, setUserStatus] = useState("all");
  const [lastActiveWithin, setLastActiveWithin] = useState("all");
  const [sendOncePerUser, setSendOncePerUser] = useState(true);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImageName, setUploadedImageName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const MAX_CHARS = 256;

  const sendNotification = async () => {
    try {
      setLoading(true);
      setResult(null);
      const data = {
        title,
        message
      }
      if(imageUrl){
        data.image = getImageUrl(imageUrl)
      }
      const payload = {
        os,
        data,
        user_status: userStatus,
        last_active_within: lastActiveWithin,
        send_once_per_user: sendOncePerUser,
      };

      const res = await notificationService.sendNotification(payload)

      const isApiSuccess = res.data?.success !== false;
      setResult({
        type: isApiSuccess ? "success" : "error",
        data: res.data,
      });
    } catch (err) {
      setResult({
        type: "error",
        data: {
          success: false,
          message:
            err?.response?.data?.message || "Failed to send notification",
          status: err?.response?.status || null,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageFileUpload = async (file) => {
    if (!file) {
      return;
    }

    if (!file.type?.startsWith("image/")) {
      setResult({
        type: "error",
        data: {
          success: false,
          message: "Please select a valid image file",
        },
      });
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "notification");
      formData.append("image", file);

      const response = await notificationService.uploadNotificationImage(formData);
      const uploadedPath = response?.data?.data?.s3_key || "";

      if (!uploadedPath) {
        throw new Error("Image upload response did not contain file path");
      }

      setImageUrl(uploadedPath);
      setUploadedImageName(file.name);

    } catch (err) {
      setResult({
        type: "error",
        data: {
          success: false,
          message: err?.data?.message || err?.message || "Failed to upload image",
        },
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const charOver = message.length > MAX_CHARS;
  const charWarn = message.length > MAX_CHARS * 0.8;
  const canSend = !loading && !uploadingImage && title.trim() && message.trim() && !charOver;
  const previewImageUrl = imageUrl ? getImageUrl(String(imageUrl).trim()) : "";

  const inputClass =
    "w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition";

  const selectClass =
    "w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition appearance-none cursor-pointer";

  const isSuccess = result?.type === "success";

  const getResultRows = (data) => {
    if (!data || typeof data !== "object") return [];
    return Object.entries(data).map(([key, value]) => ({
      key,
      value: typeof value === "object" ? JSON.stringify(value, null, 2) : String(value),
      isObject: typeof value === "object",
    }));
  };

  const resultRows = result ? getResultRows(result.data) : [];

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-stone-100 overflow-hidden">

        <div className="px-8 pt-8 pb-6 border-b border-stone-100">
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight mb-1">Push Broadcast</h1>
          <p className="text-sm text-stone-400">Send a notification to your users</p>
        </div>

        <div className="px-8 pt-6 pb-8 flex flex-col gap-5">

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Platform</label>
              <div className="relative">
                <select className={selectClass} value={os} onChange={(e) => setOs(e.target.value)}>
                  <option value="all">All Platforms</option>
                  <option value="Android">Android</option>
                  <option value="ios">iOS</option>
                  <option value="Windows">Windows</option>
                  <option value="MacOS">macOS</option>
                </select>
                <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Account Status</label>
              <div className="relative">
                <select className={selectClass} value={userStatus} onChange={(e) => setUserStatus(e.target.value)}>
                  <option value="all">All Users</option>
                  <option value="N">Active Only</option>
                  <option value="Y">Deleted Only</option>
                </select>
                <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Last Active</label>
              <div className="relative">
                <select className={selectClass} value={lastActiveWithin} onChange={(e) => setLastActiveWithin(e.target.value)}>
                  <option value="all">Any Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 justify-end">
              <label className="flex items-center gap-2 text-xs font-semibold text-stone-500 tracking-wide uppercase cursor-pointer py-2.5">
                <input
                  type="checkbox"
                  checked={sendOncePerUser}
                  onChange={(e) => setSendOncePerUser(e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-400"
                />
                Once per user
              </label>
            </div>
          </div>

          <div className="border-t border-stone-100" />

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Title</label>
            <input
              type="text"
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Message</label>
            <textarea
              rows={4}
              className={`${inputClass} resize-none leading-relaxed`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message…"
            />
            <div className="flex justify-between items-center">
              <span className={`text-xs ${charOver ? "text-red-400" : charWarn ? "text-amber-400" : "text-stone-300"}`}>
                {charOver ? "Too long" : charWarn ? "Approaching limit" : ""}
              </span>
              <span className={`text-xs font-medium tabular-nums ${
                charOver ? "text-red-400" : charWarn ? "text-amber-400" : "text-stone-300"
              }`}>
                {message.length} / {MAX_CHARS}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-stone-500 tracking-wide uppercase">Image URL</label>
              <span className="text-xs text-stone-400 bg-stone-100 rounded-md px-2 py-0.5">optional</span>
            </div>

            <div className="flex items-center gap-3">
              <ImgCrop
                aspect={NOTIFICATION_IMAGE_ASPECT}
                rotationSlider
                quality={1}
              >
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  disabled={uploadingImage}
                  beforeUpload={(file) => {
                    handleImageFileUpload(file);
                    return false;
                  }}
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 bg-stone-900 text-white text-xs font-semibold rounded-lg px-3 py-2 cursor-pointer hover:bg-stone-800 transition"
                    disabled={uploadingImage}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                    {uploadingImage ? "Uploading..." : "Upload Image"}
                  </button>
                </Upload>
              </ImgCrop>
              {uploadedImageName && (
                <span className="text-xs text-stone-500 truncate">{uploadedImageName}</span>
              )}
            </div>

            <input
              type="text"
              className={inputClass}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />

            {previewImageUrl && (
              <div className="mt-1 rounded-xl border border-stone-200 bg-stone-50 p-2">
                <img
                  src={previewImageUrl}
                  alt="Notification"
                  className="w-full h-52 object-contain rounded-lg"
                />
                <p className="mt-2 text-[11px] text-stone-400 break-all">
                  {previewImageUrl}
                </p>
              </div>
            )}
          </div>

          <button
            onClick={sendNotification}
            disabled={!canSend}
            className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all duration-150
              ${canSend
                ? "bg-stone-900 text-white hover:bg-stone-800 active:scale-95 shadow-sm"
                : "bg-stone-200 text-stone-400 cursor-not-allowed"
              }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                  <path d="M14 2L2 7.5L7 8.5M14 2L9 14L7 8.5M14 2L7 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Send Notification
              </>
            )}
          </button>

          {result && (
            <div className={`rounded-2xl border overflow-hidden ${
              isSuccess ? "border-green-200" : "border-red-200"
            }`}>

              <div className={`flex items-center gap-3 px-5 py-4 ${
                isSuccess ? "bg-green-50" : "bg-red-50"
              }`}>
                <div className={`flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${
                  isSuccess ? "bg-green-100" : "bg-red-100"
                }`}>
                  {isSuccess ? (
                    <svg className="w-4 h-4 text-green-600" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-red-500" viewBox="0 0 16 16" fill="none">
                      <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-tight ${
                    isSuccess ? "text-green-800" : "text-red-700"
                  }`}>
                    {isSuccess ? "Notification Sent" : "Delivery Failed"}
                  </p>
                  <p className={`text-xs mt-0.5 truncate ${
                    isSuccess ? "text-green-600" : "text-red-500"
                  }`}>
                    {isSuccess
                      ? result.data?.message || "Your notification was broadcast successfully."
                      : result.data?.message || result.data?.error || "Something went wrong. Please try again."}
                  </p>
                </div>

                <button
                  onClick={() => setResult(null)}
                  className={`shrink-0 p-1.5 rounded-lg transition ${
                    isSuccess
                      ? "text-green-400 hover:bg-green-100"
                      : "text-red-300 hover:bg-red-100"
                  }`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Key-value detail rows */}
              {resultRows.length > 0 && (
                <div className={`divide-y ${isSuccess ? "divide-green-100 bg-green-50/40" : "divide-red-100 bg-red-50/40"}`}>
                  {resultRows.map(({ key, value, isObject }) => (
                    <div key={key} className="flex items-start gap-4 px-5 py-2.5">
                      <span className="text-xs font-semibold text-stone-400 uppercase tracking-wide w-24 shrink-0 pt-0.5">
                        {key}
                      </span>
                      {isObject ? (
                        <pre className="text-xs text-stone-500 font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap break-all flex-1">
                          {value}
                        </pre>
                      ) : (
                        <span className={`text-xs font-medium flex-1 break-all ${
                          key === "success"
                            ? value === "true"
                              ? "text-green-600"
                              : "text-red-500"
                            : "text-stone-600"
                        }`}>
                          {value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  );
}